import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import { WindowHeaderExtras } from '@/shared/windowing';
import { Button, WindowContent } from '@/shared/ui';
import { openJobDetailWindow } from '../internal/jobsWindows';
import { useNow } from '../internal/useNow';
import { useJobsStore } from '../jobsStore';
import { JobOverviewRow } from './components/JobOverviewRow';

/**
 * Obsah okna „Operace na pozadí" — centrální přehled všech jobů uživatele (dle designu):
 * scrollovatelný seznam řádků, prázdný stav a patička „Odstraň dokončené · Zavřít".
 */
export function JobsOverviewPage({ layerId }: { layerId: string }) {
  const { t } = useTranslation('jobs');
  const { kernel } = useNavigationContext();
  const now = useNow();
  const rows = useJobsStore(
    useShallow((s) =>
      s.order
        .map((id) => s.runs[id])
        .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined)
    )
  );
  const removeCompleted = useJobsStore((s) => s.removeCompleted);

  const { runningCount, hasCompleted } = useMemo(() => {
    let runningCount = 0;
    let hasCompleted = false;
    for (const entry of rows) {
      if (entry.summary.status === 'Running') runningCount += 1;
      if (entry.summary.status === 'Succeeded') hasCompleted = true;
    }
    return { runningCount, hasCompleted };
  }, [rows]);

  const subtitle =
    rows.length === 0
      ? t('central.subEmpty')
      : runningCount > 0
        ? t('central.subActive', { count: runningCount, total: rows.length })
        : t('central.subIdle', { count: rows.length });

  return (
    <WindowContent scroll={false} className="p-0" data-testid="jobs-overview">
      <WindowHeaderExtras subtitle={subtitle} />
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          {rows.length === 0 ? (
            <div className="text-phx-text-muted px-5 py-9 text-center text-[13px] leading-[1.6]">
              {t('central.empty')}
            </div>
          ) : (
            rows.map((entry) => (
              <JobOverviewRow
                key={entry.summary.id}
                run={entry.summary}
                compact={entry.compact}
                now={now}
                onOpenDetail={(runId) => openJobDetailWindow(kernel, runId)}
              />
            ))
          )}
        </div>
        <div className="border-phx-border-2 bg-phx-surface-2 flex shrink-0 items-center gap-2 border-t px-3 py-[9px]">
          {hasCompleted ? (
            <Button size="sm" onClick={removeCompleted}>
              {t('central.removeDone')}
            </Button>
          ) : null}
          <span className="flex-1" />
          <Button size="sm" variant="primary" onClick={() => void kernel.closeLayer(layerId)}>
            {t('central.close')}
          </Button>
        </div>
      </div>
    </WindowContent>
  );
}
